/**
 * Cross-page HTML prefetch for browsers WITHOUT the Speculation Rules API —
 * i.e. Safari and Firefox, the majority of this site's visitors. Chromium
 * browsers prerender on hover by themselves (see speculative-loading.php), so
 * this module detects that capability and stays fully inert there — otherwise
 * hover would trigger both a prerender and a duplicate fetch.
 *
 * On intent to navigate — a settled hover (65ms, instant.page's value, so
 * drive-by mouse passes don't fire) or touchstart (fires ~90ms before the
 * click completes) — for an internal link: fetch() the target HTML once per
 * URL, warming the connection and, when the response is cacheable (WP Super
 * Cache on live), the HTTP cache. Query-string URLs are skipped: those are
 * uncached filter variants.
 *
 * Detail hero images are deliberately NOT warmed here anymore: hover doesn't
 * exist on touch devices, so heroPreload.js warms them viewport-driven on
 * all engines instead.
 */
import { fastConnection } from './utils'

const supportsSpeculation = HTMLScriptElement.supports?.('speculationrules')

if (!supportsSpeculation && fastConnection()) {
  const done = new Set()

  const prefetch = (link) => {
    if (
      link.origin === location.origin &&
      link.pathname !== location.pathname &&
      !link.search &&
      !done.has(link.href)
    ) {
      done.add(link.href)
      // priority is Chromium-only and harmlessly ignored elsewhere; keepalive
      // lets the warm-up finish even if the user clicks through immediately.
      fetch(link.href, { priority: 'low', keepalive: true }).catch(() => {})
    }
  }

  let hoverTimer
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return
    const link = e.target.closest('a[href]')
    if (!link) return
    clearTimeout(hoverTimer)
    hoverTimer = setTimeout(() => prefetch(link), 65)
  })
  document.addEventListener('pointerout', () => clearTimeout(hoverTimer))

  document.addEventListener(
    'touchstart',
    (e) => {
      const link = e.target.closest('a[href]')
      if (link) prefetch(link)
    },
    { passive: true }
  )
}
